import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  HStack,
  Stack,
  Button,
  useDisclosure,
  Spinner,
  Dialog,
  Field,
  Input,
  VStack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { SortableContext } from '@dnd-kit/sortable';
import { tasksAPI, projectsAPI } from '../services/api';
import { Task, Project, Column } from '../types';
import TaskCard from './TaskCard';
import SortableColumn from './SortableColumn';
import TaskModal from './TaskModal';
import { toast } from '../utils/toast';
import { useAuth } from '../context/AuthContext';

interface KanbanBoardProps {
  projectId: string;
  project: Project;
  onProjectUpdate?: () => void;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ projectId, project, onProjectUpdate }) => {
  const { user } = useAuth();
  const creatorId = (project.createdBy as any)?._id || (project.createdBy as any)?.id;
  const isAdmin = user?.role === 'admin';
  const isMasterAdmin = user?.role === 'masterAdmin' || user?.email === 'admin@admin.com';
  const canManageColumns = !!(isAdmin || isMasterAdmin || (creatorId && user?.id && creatorId.toString() === user.id));
  const [tasks, setTasks] = useState<Task[]>([]);
  const [columns, setColumns] = useState<Column[]>(project.columns || [
    { id: 'todo', title: 'To Do', color: '#9CA3AF', order: 0 },
    { id: 'in-progress', title: 'In Progress', color: '#3B82F6', order: 1 },
    { id: 'done', title: 'Done', color: '#10B981', order: 2 }
  ]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const { open, onOpen, onClose } = useDisclosure();
  const { open: columnDialogOpen, onOpen: onColumnDialogOpen, onClose: onColumnDialogClose } = useDisclosure();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [newColumnColor, setNewColumnColor] = useState('#9CA3AF');
  const [newColumnSetAsFinished, setNewColumnSetAsFinished] = useState(false);
  const { open: deleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const [columnToDelete, setColumnToDelete] = useState<string | null>(null);
  const addColumnLabel = useBreakpointValue({ base: 'Add Row', md: 'Add Column' });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (project.columns && project.columns.length > 0) {
      setColumns(project.columns.sort((a: Column, b: Column) => a.order - b.order));
    }
  }, [project]);

  const loadTasks = useCallback(async () => {
    try {
      const response = await tasksAPI.getByProject(projectId);
      setTasks(response.data);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to load tasks',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Column reorder: active and over are column ids
    const isColumnDrag = columns.some((c) => c.id === activeId);
    if (isColumnDrag && !canManageColumns) return;
    if (isColumnDrag && columns.some((c) => c.id === overId) && activeId !== overId) {
      const oldIndex = columns.findIndex((c) => c.id === activeId);
      const newIndex = columns.findIndex((c) => c.id === overId);
      if (oldIndex !== -1 && newIndex !== -1) {
        const prevColumns = [...columns];
        const reordered = arrayMove(columns, oldIndex, newIndex);
        setColumns(reordered.map((c, i) => ({ ...c, order: i })));
        try {
          await projectsAPI.reorderColumns(projectId, reordered.map((c) => c.id));
          if (onProjectUpdate) onProjectUpdate();
        } catch (error: any) {
          toast({
            title: 'Error',
            description: error.response?.data?.message || 'Could not reorder columns',
            status: 'error',
          });
          setColumns(prevColumns);
        }
      }
      return;
    }

    // Task move
    const activeTask = tasks.find((t) => t._id === activeId);
    if (!activeTask) return;

    const canEditActiveTask = (() => {
      const isAdmin = user?.role === 'admin';
      const isMasterAdmin = user?.role === 'masterAdmin' || user?.email === 'admin@admin.com';
      const userId = user?.id;
      if (isAdmin || isMasterAdmin) return true;
      if (!userId) return false;
      const participantIds: string[] = [];
      if (Array.isArray(activeTask.participants)) {
        participantIds.push(
          ...activeTask.participants
            .map((p: any) => (p?._id || p?.id || p)?.toString?.())
            .filter(Boolean) as string[]
        );
      }
      if (activeTask.assignedTo) {
        const aId = ((activeTask.assignedTo as any)._id || (activeTask.assignedTo as any).id)?.toString();
        if (aId) participantIds.push(aId);
      }
      return participantIds.includes(userId);
    })();
    if (!canEditActiveTask) return;

    let targetColumnId = columns.find((col) => col.id === overId)?.id;
    if (!targetColumnId) {
      const overTask = tasks.find((t) => t._id === overId);
      if (overTask) targetColumnId = overTask.status;
    }
    if (targetColumnId && targetColumnId !== activeTask.status) {
      try {
        const newTasks = tasks.map((task) =>
          task._id === activeId ? { ...task, status: targetColumnId as any } : task
        );
        setTasks(newTasks);

        await tasksAPI.updateStatus(activeId, targetColumnId, activeTask.order);
        await loadTasks();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: 'Failed to update task',
          status: 'error',
        });
        await loadTasks();
      }
    }
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    onOpen();
  };

  const handleTaskSaved = () => {
    // Samo osveži zadatke; modal se zatvara eksplicitno (Zatvori, kreiranje ili brisanje)
    setSelectedTask(null);
    loadTasks();
  };

  const handleCreateTask = () => {
    setSelectedTask(null);
    onOpen();
  };

  const handleAddColumn = async () => {
    if (!newColumnTitle.trim()) {
      console.error('Column title is required');
      return;
    }

    try {
      const response = await projectsAPI.addColumn(projectId, newColumnTitle, newColumnColor, newColumnSetAsFinished);
      const newColumn = response.data;
      setColumns([...columns, newColumn].sort((a: Column, b: Column) => a.order - b.order));
      setNewColumnTitle('');
      setNewColumnColor('#9CA3AF');
      setNewColumnSetAsFinished(false);
      onColumnDialogClose();
      
      // Reload project to get updated columns
      const projectResponse = await projectsAPI.getById(projectId);
      if (projectResponse.data.columns) {
        setColumns(projectResponse.data.columns.sort((a: Column, b: Column) => a.order - b.order));
      }
      
      // Notify parent to reload project
      if (onProjectUpdate) {
        onProjectUpdate();
      }
      
      console.log('Column added successfully');
    } catch (error: any) {
      console.error('Failed to add column:', error.response?.data?.message || error.message);
    }
  };

  const handleDeleteColumn = (columnId: string) => {
    if (columns.length <= 1) {
      console.error('Cannot delete the last column');
      return;
    }

    setColumnToDelete(columnId);
    onDeleteDialogOpen();
  };

  const handleSetAsFinished = async (columnId: string) => {
    try {
      await projectsAPI.setFinishedColumn(projectId, columnId);
      if (onProjectUpdate) onProjectUpdate();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Could not set column as Finished',
        status: 'error',
      });
    }
  };

  const confirmDeleteColumn = async () => {
    if (!columnToDelete) return;

    try {
      await projectsAPI.deleteColumn(projectId, columnToDelete);
      const updatedColumns = columns.filter(col => col.id !== columnToDelete);
      setColumns(updatedColumns);
      setColumnToDelete(null);
      onDeleteDialogClose();
      
      // Reload project to get updated columns
      const projectResponse = await projectsAPI.getById(projectId);
      if (projectResponse.data.columns) {
        setColumns(projectResponse.data.columns.sort((a: Column, b: Column) => a.order - b.order));
      }
      
      // Notify parent to reload project
      if (onProjectUpdate) {
        onProjectUpdate();
      }
      
      console.log('Column deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete column:', error.response?.data?.message || error.message);
      onDeleteDialogClose();
    }
  };

  const getTasksByStatus = (status: string) => {
    return tasks.filter((task) => task.status === status);
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box width="100%">
      <HStack justifyContent="space-between" mb={8}>
        <Heading size="xl" color="gray.800">Kanban Board</Heading>
        <HStack gap={3}>
          {canManageColumns && (
          <Button 
            variant="outline"
            onClick={onColumnDialogOpen}
            borderRadius="lg"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: 'md',
            }}
          >
            <HStack gap={2}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>{addColumnLabel}</span>
            </HStack>
          </Button>
          )}
          <Button 
            bgGradient="to-r"
            gradientFrom="#273f96"
            gradientTo="#1369b3"
            color="white"
            onClick={handleCreateTask}
            borderRadius="lg"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: 'xl',
            }}
            fontWeight="semibold"
          >
            <HStack gap={2}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>New Task</span>
            </HStack>
          </Button>
        </HStack>
      </HStack>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Box
          width="100%"
          overflowX={{ base: 'visible', md: 'auto' }}
          overflowY={{ base: 'visible', md: 'visible' }}
          pb={4}
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#888 #f1f1f1',
          }}
          className="kanban-scroll"
        >
          <SortableContext items={columns.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            align="flex-start"
            gap={6}
            minW={{ base: '100%', md: 'min-content' }}
          >
          {columns.map((column) => {
            const columnTasks = getTasksByStatus(column.id);
            return (
              <SortableColumn
                key={column.id}
                column={column}
                tasks={columnTasks}
                onTaskClick={handleTaskClick}
                onDelete={canManageColumns ? handleDeleteColumn : undefined}
                isFinished={project.finishedColumnId === column.id}
                onSetAsFinished={canManageColumns && !project.finishedColumnId ? handleSetAsFinished : undefined}
                canReorderColumns={canManageColumns}
              />
            );
          })}
          </Stack>
          </SortableContext>
        </Box>
        <DragOverlay>
          {activeId ? (() => {
            const activeTask = tasks.find((t) => t._id === activeId);
            const statusLabel = activeTask ? columns.find((c) => c.id === activeTask.status)?.title : undefined;
            return activeTask ? (
              <Box opacity={0.9} transform="rotate(5deg)">
                <TaskCard task={activeTask} statusLabel={statusLabel} onClick={() => {}} />
              </Box>
            ) : null;
          })() : null}
        </DragOverlay>
      </DndContext>

      <TaskModal
        open={open}
        onClose={onClose}
        projectId={projectId}
        task={selectedTask}
        onSaved={handleTaskSaved}
      />

      {/* Add Column Dialog */}
      <Dialog.Root open={columnDialogOpen} onOpenChange={(e: { open: boolean }) => !e.open && (setNewColumnSetAsFinished(false), onColumnDialogClose())}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Add New Column</Dialog.Title>
              <Dialog.CloseTrigger />
            </Dialog.Header>
            <Dialog.Body>
              <VStack gap={4}>
                <Field.Root required>
                  <Field.Label>Column Title</Field.Label>
                  <Input
                    value={newColumnTitle}
                    onChange={(e) => setNewColumnTitle(e.target.value)}
                    placeholder="e.g., Review, Testing"
                    autoFocus
                  />
                </Field.Root>
                {!project.finishedColumnId && (
                  <Field.Root>
                    <Field.Label>
                      <HStack gap={2} as="span">
                        <Box as="span" display="inline-flex" color="gold">★</Box>
                        Column for finished tasks
                      </HStack>
                    </Field.Label>
                    <HStack gap={2}>
                      <input
                        type="checkbox"
                        id="setAsFinished"
                        checked={newColumnSetAsFinished}
                        onChange={(e) => setNewColumnSetAsFinished(e.target.checked)}
                      />
                      <label htmlFor="setAsFinished" style={{ fontSize: '14px', color: '#718096', cursor: 'pointer' }}>
                        Only one column can be marked. Tasks in it will not be suggested as dependencies.
                      </label>
                    </HStack>
                  </Field.Root>
                )}
                <Field.Root>
                  <Field.Label>Color</Field.Label>
                  <HStack gap={3} align="center">
                    <Box
                      w="60px"
                      h="40px"
                      borderRadius="md"
                      borderWidth={2}
                      borderColor="gray.300"
                      bg={newColumnColor}
                      cursor="pointer"
                      position="relative"
                      overflow="hidden"
                    >
                      <input
                        type="color"
                        value={newColumnColor}
                        onChange={(e) => setNewColumnColor(e.target.value)}
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer',
                        }}
                      />
                    </Box>
                    <Input
                      value={newColumnColor}
                      onChange={(e) => setNewColumnColor(e.target.value)}
                      placeholder="#9CA3AF"
                      maxW="120px"
                      fontFamily="mono"
                    />
                  </HStack>
                </Field.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack>
                <Button variant="ghost" onClick={() => (setNewColumnSetAsFinished(false), onColumnDialogClose())}>
                  Cancel
                </Button>
                <Button 
                  bgGradient="to-r"
                  gradientFrom="#273f96"
                  gradientTo="#1369b3"
                  color="white"
                  onClick={handleAddColumn}
                >
                  {addColumnLabel}
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      {/* Delete Column Confirmation Dialog */}
      <Dialog.Root open={deleteDialogOpen} onOpenChange={(e: { open: boolean }) => !e.open && onDeleteDialogClose()}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Delete Column</Dialog.Title>
              <Dialog.CloseTrigger />
            </Dialog.Header>
            <Dialog.Body>
              <VStack gap={2}>
                <Text>
                  Are you sure you want to delete this column? Tasks in this column will need to be moved first.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  This action cannot be undone.
                </Text>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack>
                <Button variant="ghost" onClick={onDeleteDialogClose}>
                  Cancel
                </Button>
                <Button 
                  colorScheme="red"
                  onClick={confirmDeleteColumn}
                >
                  Delete
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

export default KanbanBoard;
